import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import { PullButton } from '@shared/components/PullButton';
import { View } from '@shared/components/View';
import { NativeDropdownButton } from '@shared/components/dropdown';
import { BasicOperatorItem } from '@shared/components/filter/filterOptions/BasicOperators';
import { MobileMultiSelect } from '@shared/components/filter/filterOptions/MobileMultiSelect';
import {
  MobileTagSelections,
  RenderItem,
  toFilterTagOption,
} from '@shared/components/filter/filterOptions/MobileTagOptions';
import {
  ControlOption,
  FilterOperation,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
  FilterTagSelection,
} from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody1Bold, MBTextOverline } from '@shared/components/typography';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { MBActionSheet } from '@shared/util/actionSheet';
import { i18n } from '@shared/util/i18n';
import { useConditionTypeOptions } from './hooks/useConditionTypeOptions';
import { AutoExtendConditionType, AutoExtendOptionsProps, RepeatEndType } from './types';
import { MAX_COUNT, getInitialAutoExtendTagFilterOperator, getOperatorLabel } from './utils';

const MIN_COUNT = 1;

export function AutoExtendOptionsMobile({
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  showingTagFilterOptions = false,
  setShowingTagFilterOptions,
  conditionType,
  setConditionType,
  setRepeatEndType,
}: AutoExtendOptionsProps) {
  const hasDefaultAutoExtend =
    !isNil(component?.autoExtendDefault?.minimumInSeries) &&
    !isNil(component?.autoExtendDefault?.impliedFilterComponentId);

  const conditionTypeOptions = useConditionTypeOptions({ component });
  const conditionTypeMenuContainerRef = useRef<MBTouchableOpacity>(null);
  const [showConditionTypeMenu, setShowConditionTypeMenu] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<string>(
    getInitialAutoExtendTagFilterOperator(tagFilter),
  );

  // Handle selecting or deselecting a condition type option
  const handleConditionTypeSelect = useCallback(
    (selectedType: AutoExtendConditionType) => {
      const nextType: AutoExtendConditionType | undefined =
        conditionType === selectedType ? undefined : selectedType;

      setConditionType(nextType);

      if (nextType === 'default') {
        setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? undefined);
        setTagFilter({
          componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
          value: {
            containsString: null,
            isEmpty: null,
            stringWildcard: null,
            containsNumber: null,
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            numberIs: null,
            containsCurrentUser: null,
          },
        });
        setRepeatEndType(RepeatEndType.NEVER);
      } else if (nextType === 'custom') {
        setMinimumInSeries(MAX_COUNT);
        setTagFilter(undefined);
      } else {
        // Clearing – remove all values
        setMinimumInSeries(undefined);
        setTagFilter(undefined);
        setRepeatEndType(RepeatEndType.END_DATE);
      }
    },
    [
      conditionType,
      component,
      setMinimumInSeries,
      setTagFilter,
      setConditionType,
      setRepeatEndType,
    ],
  );

  const handleOpenConditionTypeMenu = () => {
    setShowConditionTypeMenu(true);

    MBActionSheet.show({
      parent: conditionTypeMenuContainerRef.current ?? undefined,
      webVerticalAlignment: 'above',
      webHorizontalAlignment: 'left',
      onClose: () => setShowConditionTypeMenu(false),
      options: conditionTypeOptions.map(opt => ({
        title: opt.label ?? '',
        onPress: () => handleConditionTypeSelect(opt.id as AutoExtendConditionType),
      })),
    });
  };

  useEffect(() => {
    if (conditionTypeOptions.length === 1) {
      const onlyOpt = conditionTypeOptions[0].id as AutoExtendConditionType;
      if (conditionType !== onlyOpt) {
        setConditionType(onlyOpt);
      }
    }
  }, [conditionTypeOptions, conditionType, setConditionType]);

  const countIsOutOfRange =
    isNil(minimumInSeries) || minimumInSeries < MIN_COUNT || minimumInSeries > MAX_COUNT;

  const handleMinimumInSeriesChange = (newCount: string) => {
    if (newCount === '') {
      setMinimumInSeries(undefined);
      return;
    }
    const num = Number(newCount);
    if (!isNaN(num)) {
      setMinimumInSeries(num);
    }
  };

  return (
    <View paddingTop={1.5}>
      {!showingTagFilterOptions && (
        <View marginBottom={1.5}>
          <MBTextBody1Bold>
            {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.title')}
          </MBTextBody1Bold>
        </View>
      )}
      {hasDefaultAutoExtend && !showingTagFilterOptions && (
        <View marginBottom={conditionType === 'custom' ? 1.5 : 0}>
          <NativeDropdownButton
            containerRef={conditionTypeMenuContainerRef}
            onPress={handleOpenConditionTypeMenu}
            disabled={!showConditionTypeMenu}
            style={{ marginLeft: 0 }}
          >
            <MBTextBody1
              numberOfLines={1}
              color={conditionType ? Colors.v2.purple : Colors.v2.gray50}
            >
              {conditionTypeOptions.find(o => o.id === conditionType)?.label ??
                i18n('workflowTemplate.component.dateAndRepeat.autoExtend.setConditionPlaceholder')}
            </MBTextBody1>
          </NativeDropdownButton>
        </View>
      )}

      {conditionType === 'custom' && !showingTagFilterOptions && (
        <>
          <View>
            <View marginBottom={1.5}>
              <View row centerVertically>
                <MBTextInput
                  value={String(minimumInSeries ?? '')}
                  onChangeText={handleMinimumInSeriesChange}
                  style={[
                    {
                      width: grid(7),
                      height: grid(5.5),
                      paddingVertical: 0,
                      paddingHorizontal: grid(1),
                      backgroundColor: Colors.v2.backgroundGrayLight,
                      borderWidth: 0,
                      marginRight: grid(1),
                    },
                    countIsOutOfRange && { borderWidth: 1, borderColor: Colors.v2.red },
                  ]}
                  placeholder="1-100"
                />
                <View marginBottom={1}>
                  <MBTextBody1>
                    {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.minimumInSeries', {
                      count: minimumInSeries ?? 0,
                    })}
                  </MBTextBody1>
                </View>
              </View>
            </View>
          </View>
          <TouchableOpacity
            style={{
              paddingVertical: grid(0.5),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            onPress={() => setShowingTagFilterOptions?.(true)}
          >
            <View
              row
              centerVertically
              style={{
                width: '85%',
                justifyContent: 'flex-start',
              }}
            >
              <TagFilterSummary
                tagFilter={tagFilter}
                workflowTemplateId={workflowTemplateId}
                selectedOperator={selectedOperator}
              />
            </View>
            <MBIconV2 name="arrow-point-right" color={Colors.v2.purple} size={grid(3.5)} />
          </TouchableOpacity>
        </>
      )}

      {conditionType === 'custom' && showingTagFilterOptions && (
        <TagFilter
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          workflowTemplateId={workflowTemplateId}
          selectedOperator={selectedOperator}
          setSelectedOperator={setSelectedOperator}
        />
      )}
    </View>
  );
}

function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  selectedOperator,
  setSelectedOperator,
}: {
  tagFilter?: MBAPI.FilterByComponent;
  setTagFilter: (tagFilter?: MBAPI.FilterByComponent) => void;
  workflowTemplateId: string;
  selectedOperator: string;
  setSelectedOperator: (operator: string) => void;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const operatorLabel = getOperatorLabel(selectedOperator);
  const tagComponents = useMemo(
    () =>
      (workflowTemplate?.components ?? []).filter(
        isTagComponent,
      ) as MBAPI.WorkflowTemplate_components_TagComponent[],
    [workflowTemplate],
  );

  const tagComponentOptions: ControlOption[] = tagComponents.map(tc => ({
    id: tc.id,
    label: tc.label ?? '',
    icon: tc.icon as MBIconV2Name,
  }));

  const selectedTagComponentOption = tagComponentOptions.find(o => o.id === tagFilter?.componentId);
  const selectedTagComponent = useMemo(() => {
    return workflowTemplate?.components.find(c => c.id === tagFilter?.componentId) as
      | MBAPI.WorkflowTemplate_components_TagComponent
      | undefined;
  }, [workflowTemplate, tagFilter]);

  const isMultiSelect = selectedTagComponent?.multiselect ?? false;

  const filterUuidRef = useRef<string>(uuidv4());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }

    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label,
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    } as ControlOption;
  }, [selectedTagComponent]);

  /**
   * Build the `selectedOptions` map expected by `MobileTagSelections`. The key MUST match
   * `filterOption.uuid` so the component can locate selections.
   */
  const selectedOptions = useMemo(() => {
    const map = new Map<string, FilterSelectionState>();

    if (!filterOption) {
      return map;
    }

    let selections: FilterTagOption[] = [];

    const tagValues = tagFilter?.value?.containsString?.values;

    if (tagValues && tagValues.length > 0) {
      selections = tagValues.map(tv => {
        const opt = selectedTagComponent?.options?.find(o => o.value === tv);
        return {
          ...(opt || { value: tv, label: String(tv) }),
        } as FilterTagOption;
      });
    }

    map.set(filterOption.uuid!, {
      filter: filterOption,
      selection: selections as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    });

    return map;
  }, [filterOption, selectedTagComponent, tagFilter, selectedOperator]);

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !filterOption) {
      return;
    }

    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: (selection ?? []) as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }
  };

  const handleTagComponentOptionChange = (option: ControlOption) => {
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    const newFilter: MBAPI.FilterByComponent = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as MBAPI.FilterByComponent;

    setTagFilter(newFilter);
  };

  const handleClearAllOptions = () => {
    if (!tagFilter) {
      return;
    }
    const clearedFilter: MBAPI.FilterByComponent = {
      componentId: tagFilter.componentId,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    };
    setTagFilter(clearedFilter);
  };

  // Narrow the raw selections to the expected tag-selection array shape.
  const isFilterTagSelectionArray = (
    selections: FilterSelection,
  ): selections is FilterTagSelection[] =>
    Array.isArray(selections) && selections.every(s => typeof s === 'object' && 'value' in s);

  const rawSelections: FilterSelection | undefined = !isNil(filterOption?.uuid)
    ? selectedOptions.get(filterOption.uuid)?.selection
    : undefined;

  const tagComponent =
    !isNil(filterOption?.component) && isTagComponent(filterOption?.component)
      ? filterOption?.component
      : undefined;

  const tagSelections: FilterTagSelection[] =
    rawSelections && isFilterTagSelectionArray(rawSelections) ? rawSelections : [];

  const emptyLabel = i18n('card.actions.filters.emptyLabel', { label: filterOption?.label ?? '' });
  const canonicalTagOptions: FilterTagOption[] = (tagComponent?.options ?? []).map(o => ({
    value: o.value,
    label: o.label ?? '',
    backgroundColor: o.backgroundColor as FilterTagOption['backgroundColor'],
    archived: o.archived ?? undefined,
  }));
  const selectedTagOptions: FilterTagOption[] = tagSelections.map(sel =>
    toFilterTagOption(sel, canonicalTagOptions, emptyLabel),
  );
  const availableTagOptions: FilterTagOption[] = (tagComponent?.options ?? []).map(o => ({
    value: o.value,
    label: o.label ?? '',
    backgroundColor: o.backgroundColor as FilterTagOption['backgroundColor'],
    archived: o.archived ?? undefined,
  }));

  return (
    <View>
      <PullButton
        renderSelected={() => (
          <View row centerVertically>
            {selectedTagComponentOption?.icon && (
              <MBIconV2
                name={selectedTagComponentOption.icon as MBIconV2Name}
                color={Colors.v2.purple}
                size={grid(3)}
                style={{ marginRight: grid(1) }}
              />
            )}
            <MBTextBody1
              numberOfLines={1}
              color={selectedTagComponentOption ? Colors.v2.purple : Colors.v2.gray50}
            >
              {selectedTagComponentOption?.label ?? i18n('card.actions.groups.noneSelected')}
            </MBTextBody1>
          </View>
        )}
        renderOptions={onSelect => (
          <View>
            {tagComponentOptions.map(option => (
              <TouchableOpacity
                key={option.id}
                onPress={() => {
                  onSelect();
                  handleTagComponentOptionChange(option);
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: grid(1.5),
                }}
              >
                <View row centerVertically>
                  {option.icon && (
                    <MBIconV2
                      name={option.icon as MBIconV2Name}
                      color={Colors.v2.purple}
                      size={grid(3)}
                      style={{ marginRight: grid(1) }}
                    />
                  )}
                  <MBTextBody1>{option.label}</MBTextBody1>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
      />
      {filterOption && (
        <PullButton
          renderSelected={() => <BasicOperatorItem text={operatorLabel} />}
          renderOptions={onSelect => (
            <>
              {selectedOperator !== 'contains' && (
                <TouchableOpacity
                  onPress={() => {
                    onSelect();
                    setSelectedOperator('contains');
                    handleSetOption(filterOption, 'updateSelections', tagSelections);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: grid(1.5),
                  }}
                >
                  <BasicOperatorItem
                    text={i18n('card.actions.filters.fields.common.operator.autoContains')}
                  />
                </TouchableOpacity>
              )}
              {selectedOperator !== 'does-not-contain' && (
                <TouchableOpacity
                  onPress={() => {
                    onSelect();
                    setSelectedOperator('does-not-contain');
                    handleSetOption(filterOption, 'updateSelections', undefined);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: grid(1.5),
                  }}
                >
                  <BasicOperatorItem
                    text={i18n('card.actions.filters.fields.common.operator.doesNotContainMenu')}
                  />
                </TouchableOpacity>
              )}
            </>
          )}
        />
      )}
      {filterOption && selectedOperator === 'does-not-contain' && (
        <MobileMultiSelect
          onChange={selected => handleSetOption(filterOption, 'updateSelections', selected)}
          onDeselectAll={() => handleClearAllOptions()}
          selectedOptions={selectedTagOptions}
          options={availableTagOptions}
          paddingHorizontal={0}
          renderItem={({ data }) => <RenderItem data={data} />}
          isMultiSelect={isMultiSelect}
        />
      )}
    </View>
  );
}

// Summary row component to display selected tag filter info when options sheet is closed
function TagFilterSummary({
  tagFilter,
  workflowTemplateId,
  selectedOperator,
}: {
  tagFilter?: MBAPI.FilterByComponent;
  workflowTemplateId: string;
  selectedOperator: string;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const selectedTagComponent = useMemo(() => {
    return workflowTemplate?.components.find(c => c.id === tagFilter?.componentId) as
      | MBAPI.WorkflowTemplate_components_TagComponent
      | undefined;
  }, [workflowTemplate, tagFilter]);

  const label =
    selectedTagComponent?.label ??
    i18n('workflowTemplate.component.dateAndRepeat.autoExtend.noneSelectedTagFilterLabel');

  const tagValues = tagFilter?.value?.containsString?.values;
  const isNegated = tagFilter?.value?.containsString?.negate;
  const hasTagsSelected = !isEmpty(tagValues);

  // Build a FilterOption representing the selected tag component so we can reuse
  // existing utility components like `MobileTagSelections` for display.
  const filterUuidRef = useRef<string>(uuidv4());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }

    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label,
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    } as ControlOption;
  }, [selectedTagComponent]);

  /**
   * Build the `selectedOptions` map expected by `MobileTagSelections`. The key MUST match
   * `filterOption.uuid` so the component can locate selections.
   */
  const selectedOptions = useMemo(() => {
    const map = new Map<string, FilterSelectionState>();

    if (!filterOption) {
      return map;
    }

    let selections: FilterTagOption[] = [];

    if (tagValues && tagValues.length > 0) {
      selections = tagValues.map(tv => {
        const opt = selectedTagComponent?.options?.find(o => o.value === tv);
        return {
          ...(opt || { value: tv, label: String(tv) }),
        } as FilterTagOption;
      });
    }

    map.set(filterOption.uuid!, {
      filter: filterOption,
      selection: selections as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    });

    return map;
  }, [filterOption, selectedTagComponent, tagValues, selectedOperator]);

  const hasSelections = hasTagsSelected;

  return (
    <View style={{ maxWidth: '85%', flexShrink: 0, flexGrow: 0, flexBasis: '100%' }}>
      {!hasSelections && !filterOption && (
        <MBTextBody1 color={Colors.v2.gray50}>{label}</MBTextBody1>
      )}
      {filterOption && (
        <View style={{ width: '100%' }} marginBottom={0.5}>
          <MBTextOverline>{label}</MBTextOverline>
        </View>
      )}
      {filterOption && (
        <View row centerVertically>
          <View marginRight={1}>
            <MBTextBody1 color={Colors.v2.gray50}>
              {!isNegated && i18n('card.actions.filters.fields.common.operator.autoContains')}
              {isNegated &&
                i18n('card.actions.filters.fields.common.operator.doesNotContainSelected')}
            </MBTextBody1>
          </View>
          {isNegated && (
            <MobileTagSelections filter={filterOption} selectedOptions={selectedOptions} />
          )}
        </View>
      )}
    </View>
  );
}
